/* istanbul ignore file */
import React from 'react';
import ReactDOM from 'react-dom';
import mapboxgl from 'mapbox-gl';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import SmartlookClient from 'smartlook-client';
import { shouldUseMirage } from 'utilities/apiConstants';
import * as DatadogManager from './utilities/datadog/datadogManager';
import App from './components/App';
import * as serviceWorker from './serviceWorker';
import applyUrlCropSeasons from './utilities/applyUrlCropSeasons';
import '@agconnections/grow-ui/dist/main.css';
import { ProvideAuth, cleanNewUserSession } from './utilities/base-auth';
import { AmplitudeContextProvider } from './utilities/amplitude/useAmplitude';
import './tailwind.css';
import './index.css';

function getGitTagNameOnly(str) {
  // Replace /, ., and - with an empty string to trim the build tag meeting sentry standards
  return str?.length > 0 ? str.replace(/[/.-]/g, '') : 'v1.0.0';
}

Sentry.init({
  release: getGitTagNameOnly(process.env.SENTRY_CODEBUILD_WEBHOOK_TRIGGER),
  dsn: "https://3dc4f6ed59a640eda9f9af2aba42b0f2@o49647.ingest.sentry.io/6555748",
  integrations: [new BrowserTracing()],
  environment: "prod",
  tracesSampleRate: parseFloat("0.1") || 0.1
});

DatadogManager.initialize();

SmartlookClient.init("2e4e8daf83c4937253a99516982b6482e03be4f5", {
  region: "us",
  version: "nextgen"
});

// react-mapbox-gl's CJS build assigns the token to an interop copy of the
// mapbox-gl module, so it never reaches the real mapbox-gl config — including
// the `accessToken` option the ReactMapboxGl() factories receive. This line is
// therefore the only assignment that actually reaches mapbox-gl; the factory
// options alone do not work. Set it once, before any map factory runs.
mapboxgl.accessToken = "pk.eyJ1Ijoic3RyaWRlcmVuZ2luZWVyaW5nIiwiYSI6ImNsdWs5ZG1vNzA2azYyam1scHljMjNyc3kifQ.YBj-8NtAHftqXroxqmbxXw";

async function bootstrap() {
  if (shouldUseMirage) {
    // Dynamic import keeps mirage (and its fixtures) out of the production bundle.
    const { makeServer } = await import('./mirage');
    makeServer({ environment: "production" });
  }
  cleanNewUserSession(); // if new login on accounts portal, clear session on main app

  // Seed the crop-season selection from ?seasonIds before the breadcrumb mounts
  // (see applyUrlCropSeasons), so an application opened from the product view
  // lands on its own season.
  applyUrlCropSeasons();

  globalThis.__CW_INITIAL_PAGE_LOAD = true;

  ReactDOM.render(
    <AmplitudeContextProvider>
      <ProvideAuth>
        <App />
      </ProvideAuth>
    </AmplitudeContextProvider>,
    document.getElementById('root')
  );

  // If you want your app to work offline and load faster, you can change
  // unregister() to register() below. Note this comes with some pitfalls.
  // Learn more about service workers: https://bit.ly/CRA-PWA

  serviceWorker.unregister();
}

bootstrap().catch(error => {
  // Without this, a failed mirage import (or anything thrown before render)
  // is a silent unhandled rejection and the page stays blank.
  // eslint-disable-next-line no-console
  console.error('App bootstrap failed:', error);
  throw error;
});
